import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Settings, Bell, Shield, User, Globe, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import LanguageSelector from '../components/LanguageSelector'

export default function SettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { currentLanguage, changeLanguage, isLoading } = useLanguage()
  const [activeSection, setActiveSection] = useState('language')
  const [saveMessage, setSaveMessage] = useState('')

  const handleLanguageChange = async (languageCode) => {
    const success = await changeLanguage(languageCode)
    if (success) {
      setSaveMessage(t('settings.languageSaved'))
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(t('settings.languageSaveError'))
    }
  }

  const settingsSections = [
    {
      id: 'language',
      title: t('settings.language'),
      icon: Globe,
      description: t('settings.selectLanguage')
    },
    {
      id: 'notifications',
      title: t('settings.notifications'),
      icon: Bell,
      description: 'Manage your notification preferences'
    },
    {
      id: 'privacy',
      title: t('settings.privacy'),
      icon: Shield,
      description: 'Control your privacy settings'
    },
    {
      id: 'account',
      title: t('settings.account'),
      icon: User,
      description: 'Manage your account details'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('settings.title')}</h1>
          <p className="mt-2 text-gray-600">Manage your preferences and account settings</p>
        </div>

        {saveMessage && (
          <div className={`mb-6 p-4 rounded-lg ${saveMessage.includes(t('settings.languageSaveError')) ? 'bg-danger-50 text-danger-700 border border-danger-200' : 'bg-success-50 text-success-700 border border-success-200'}`}>
            {saveMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="card overflow-hidden">
              <nav className="divide-y divide-gray-200">
                {settingsSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center px-4 py-4 text-left hover:bg-gray-50 transition-colors ${
                      activeSection === section.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                    }`}
                  >
                    <section.icon className={`h-5 w-5 ${activeSection === section.id ? 'text-primary-600' : 'text-gray-400'}`} />
                    <span className={`ml-3 font-medium ${activeSection === section.id ? 'text-primary-900' : 'text-gray-700'}`}>
                      {section.title}
                    </span>
                    <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-2">
            <div className="card">
              {activeSection === 'language' && (
                <div>
                  <div className="flex items-center mb-6">
                    <Globe className="h-6 w-6 text-primary-600 mr-3" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{t('settings.language')}</h2>
                      <p className="text-sm text-gray-500">{t('settings.selectLanguage')}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('language.currentLanguage')}
                      </label>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{currentLanguage.flag}</span>
                          <div>
                            <p className="font-medium text-gray-900">{currentLanguage.nativeName}</p>
                            <p className="text-sm text-gray-500">{currentLanguage.name}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('language.switchLanguage')}
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {['en', 'af', 'zu', 'xh'].map((langCode) => {
                          const lang = {
                            en: { name: t('language.english'), flag: '🇿🇦' },
                            af: { name: t('language.afrikaans'), flag: '🇿🇦' },
                            zu: { name: t('language.isizulu'), flag: '🇿🇦' },
                            xh: { name: t('language.isixhosa'), flag: '🇿🇦' }
                          }[langCode]
                          
                          return (
                            <button
                              key={langCode}
                              onClick={() => handleLanguageChange(langCode)}
                              disabled={isLoading || currentLanguage.code === langCode}
                              className={`flex items-center p-3 rounded-lg border transition-colors ${
                                currentLanguage.code === langCode
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <span className="text-xl mr-3">{lang.flag}</span>
                              <span className={`flex-1 text-left ${currentLanguage.code === langCode ? 'font-medium text-primary-900' : 'text-gray-700'}`}>
                                {lang.name}
                              </span>
                              {currentLanguage.code === langCode && (
                                <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'notifications' && (
                <div>
                  <div className="flex items-center mb-6">
                    <Bell className="h-6 w-6 text-primary-600 mr-3" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{t('settings.notifications')}</h2>
                      <p className="text-sm text-gray-500">Manage your notification preferences</p>
                    </div>
                  </div>
                  <div className="text-center py-8 text-gray-500">
                    Notification settings coming soon
                  </div>
                </div>
              )}

              {activeSection === 'privacy' && (
                <div>
                  <div className="flex items-center mb-6">
                    <Shield className="h-6 w-6 text-primary-600 mr-3" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{t('settings.privacy')}</h2>
                      <p className="text-sm text-gray-500">Control your privacy settings</p>
                    </div>
                  </div>
                  <div className="text-center py-8 text-gray-500">
                    Privacy settings coming soon
                  </div>
                </div>
              )}

              {activeSection === 'account' && (
                <div>
                  <div className="flex items-center mb-6">
                    <User className="h-6 w-6 text-primary-600 mr-3" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{t('settings.account')}</h2>
                      <p className="text-sm text-gray-500">Manage your account details</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">{t('auth.email')}</label>
                      <p className="mt-1 text-gray-900">{profile?.email || 'Not set'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">{t('auth.fullName')}</label>
                      <p className="mt-1 text-gray-900">{profile ? `${profile.first_name} ${profile.last_name}` : 'Not set'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">{t('common.role')}</label>
                      <p className="mt-1 text-gray-900 capitalize">{profile?.role?.replace('_', ' ') || 'Not set'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
